/**
 * Slice 9.1 — Compliance panel.
 *
 * Two sections stacked:
 *
 *   1. Policy Assignments — which policies are evaluating this project,
 *      with an "Add policy" affordance for admin/analyst users.
 *
 *   2. Compliance Matrix — assets × policies grid showing
 *      Compliant / Non_compliant / NA for each pair, plus a drill-down
 *      modal showing the specific rule violations.
 *
 * Read endpoint: GET /api/v1/projects/:id/policy-assignments
 *                GET /api/v1/projects/:id/compliance-assertions
 * Mutate:        POST /api/v1/projects/:id/policy-assignments
 *                DELETE /api/v1/projects/:id/policy-assignments/:assignmentId
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, apiUrl } from '../api/client';
import type { InsightDocument } from '../api/insights';
import { useAuth } from '../auth/AuthContext';
import { Modal } from './admin-ui';

interface PolicyAssignment {
  id: string;
  policy_id: string;
  is_required: number | boolean;
  short_code: string;
  name: string;
  description: string | null;
  version: string;
  source_citation: string;
  is_active: number | boolean;
  kb_origin: 'System' | 'Tenant';
}

interface ViolationDetail {
  policy_short_code: string;
  rule_id: string;
  artifact_id: string;
  artifact_type: string;
  artifact_label: string;
  artifact_algorithm: string | null;
  artifact_key_length: number | null;
  artifact_curve: string | null;
  rule_algorithm: string | null;
  rule_usage_scope: string;
  rule_min_key_size: number | null;
  rule_max_key_size: number | null;
  rule_verdict: 'Allowed' | 'Not_allowed' | 'Conditional';
  rule_condition_text: string | null;
  rule_source_citation: string | null;
  message: string;
}

interface EvaluationDetail {
  artifact_label: string;
  artifact_type: string;
  artifact_algorithm: string | null;
  artifact_key_length: number | null;
  rule_algorithm: string | null;
  rule_usage_scope: string;
  rule_verdict: 'Allowed' | 'Not_allowed' | 'Conditional';
  outcome: 'Pass' | 'Violation' | 'Conditional';
  rule_source_citation: string | null;
}

interface ComplianceAssertion {
  id: string;
  asset_id: string;
  policy_id: string;
  assertion: 'Compliant' | 'Non_compliant' | 'NA' | 'Manual_override';
  violation_count: number;
  rule_count: number;
  evaluation_details_json: {
    policy_short_code?: string;
    policy_name?: string;
    conditional_count?: number;
    violations?: ViolationDetail[];
    conditionals?: ViolationDetail[];
    evaluations?: EvaluationDetail[];
  } | null;
  asset_name: string;
  policy_short_code: string;
  policy_name: string;
  evaluated_at: string;
}

interface PolicyOption {
  id: string;
  short_code: string;
  name: string;
  version: string;
  kb_origin: 'System' | 'Tenant';
  rule_count: number;
}

export function CompliancePanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'analyst');

  const assignmentsQuery = useQuery<{ data: PolicyAssignment[] }>({
    queryKey: ['policy-assignments', projectId],
    queryFn: () => api.get<PolicyAssignment[]>(`/projects/${projectId}/policy-assignments`).then(data => ({ data })),
  });

  const assertionsQuery = useQuery<{ data: ComplianceAssertion[] }>({
    queryKey: ['compliance-assertions', projectId],
    queryFn: () => api.get<ComplianceAssertion[]>(`/projects/${projectId}/compliance-assertions`).then(data => ({ data })),
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [drilldown, setDrilldown] = useState<ComplianceAssertion | null>(null);

  if (assignmentsQuery.isLoading || assertionsQuery.isLoading) {
    return <div className="p-6 text-ink-500 text-sm italic">Loading compliance data…</div>;
  }
  if (assignmentsQuery.isError) {
    return <div className="p-6 text-red-700 text-sm">Failed to load policy assignments.</div>;
  }

  const assignments = assignmentsQuery.data?.data ?? [];
  const assertions = assertionsQuery.data?.data ?? [];

  return (
    <div>
      <PolicyAssignmentsSection
        projectId={projectId}
        assignments={assignments}
        canEdit={canEdit}
        onAddClick={() => setShowAddModal(true)}
        onRemoved={() => queryClient.invalidateQueries({ queryKey: ['policy-assignments', projectId] })}
      />

      <ComplianceMatrixSection
        projectId={projectId}
        assignments={assignments}
        assertions={assertions}
        onDrilldown={setDrilldown}
      />

      {showAddModal && (
        <AddPolicyModal
          projectId={projectId}
          existingAssignments={assignments}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['policy-assignments', projectId] });
            setShowAddModal(false);
          }}
        />
      )}

      {drilldown && (
        <DrilldownModal assertion={drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}

function PolicyAssignmentsSection({
  projectId, assignments, canEdit, onAddClick, onRemoved,
}: {
  projectId: string;
  assignments: PolicyAssignment[];
  canEdit: boolean;
  onAddClick: () => void;
  onRemoved: () => void;
}) {
  const removeMut = useMutation({
    mutationFn: async (assignmentId: string) => {
      await fetch(apiUrl(`/projects/${projectId}/policy-assignments/${assignmentId}`), {
        method: 'DELETE',
        credentials: 'same-origin',
      });
    },
    onSuccess: onRemoved,
  });

  return (
    <section className="mb-8">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-display text-ink">Assigned policies</h2>
        {canEdit && (
          <button
            type="button"
            onClick={onAddClick}
            className="text-xs px-3 py-1 bg-accent text-white rounded hover:bg-accent-hover"
          >
            Add policy
          </button>
        )}
      </div>
      {assignments.length === 0 ? (
        <div className="border border-dashed border-ink-200 rounded p-6 text-center">
          <p className="text-ink-500 text-sm">
            No policies assigned. Compliance scoring is currently inactive.
          </p>
          <p className="text-ink-400 text-xs mt-2">
            Add a bundled policy (CNSA 2.0, FIPS 140-3, etc.) to start evaluating assets.
          </p>
        </div>
      ) : (
        <div className="border border-ink-200 rounded overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">Policy</th>
                <th className="px-3 py-2 text-left">Origin</th>
                <th className="px-3 py-2 text-left">Source</th>
                {canEdit && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-ink">{a.name}</div>
                    {a.description && (
                      <div className="text-xs text-ink-400 mt-0.5 line-clamp-2">
                        {a.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded ${a.kb_origin === 'System' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {a.kb_origin}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-500 max-w-md truncate" title={a.source_citation}>
                    {a.source_citation}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Remove ${a.name} from this project?`)) {
                            removeMut.mutate(a.id);
                          }
                        }}
                        className="text-xs text-red-700 hover:underline"
                        disabled={removeMut.isPending}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ComplianceMatrixSection({
  projectId, assignments, assertions, onDrilldown,
}: {
  projectId: string;
  assignments: PolicyAssignment[];
  assertions: ComplianceAssertion[];
  onDrilldown: (a: ComplianceAssertion) => void;
}) {
  // Group assertions by asset_id
  const byAsset = useMemo(() => {
    const m = new Map<string, { name: string; byPolicy: Map<string, ComplianceAssertion> }>();
    for (const a of assertions) {
      if (!m.has(a.asset_id)) {
        m.set(a.asset_id, { name: a.asset_name, byPolicy: new Map() });
      }
      m.get(a.asset_id)!.byPolicy.set(a.policy_id, a);
    }
    return Array.from(m.entries())
      .map(([id, v]) => ({ asset_id: id, asset_name: v.name, byPolicy: v.byPolicy }))
      .sort((a, b) => a.asset_name.localeCompare(b.asset_name));
  }, [assertions]);

  // Assessed assets with no compliance result: say why rather than leave them out silently (C-029).
  const insights = useQuery({
    queryKey: ['project-insights', projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
    retry: false,
  });
  const notAssessed = useMemo(() => {
    const evaluated = new Set(assertions.map((a) => a.asset_id));
    const rows = (insights.data?.risk_register ?? []).filter((r) => !evaluated.has(r.asset_id));
    return {
      noInventory: rows.filter((r) => r.flags.includes('No crypto inventory')).map((r) => r.asset_name).sort(),
      other: rows.filter((r) => !r.flags.includes('No crypto inventory')).map((r) => r.asset_name).sort(),
    };
  }, [assertions, insights.data]);
  const names = (xs: string[]) => `${xs.slice(0, 8).join(', ')}${xs.length > 8 ? ` and ${xs.length - 8} more` : ''}`;

  if (assignments.length === 0) {
    return null;
  }

  if (byAsset.length === 0) {
    return (
      <section>
        <h2 className="text-lg font-display text-ink mb-3">Compliance results</h2>
        <div className="border border-dashed border-ink-200 rounded p-6 text-center">
          <p className="text-ink-500 text-sm">
            No compliance evaluations yet. Run an assessment to populate this view.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="text-lg font-display text-ink mb-3">Compliance results</h2>
      <p className="text-xs text-ink-500 mb-3">
        Click any cell to see the specific rule violations. Results refresh on each assessment run.
      </p>
      <div className="border border-ink-200 rounded overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left sticky left-0 bg-ink-50">Asset</th>
              {assignments.map((a) => (
                <th key={a.policy_id} className="px-3 py-2 text-left whitespace-nowrap" title={a.name}>
                  {a.short_code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {byAsset.map((row) => (
              <tr key={row.asset_id}>
                <td className="px-3 py-2 sticky left-0 bg-white whitespace-nowrap font-medium">
                  {row.asset_name}
                </td>
                {assignments.map((a) => {
                  const cell = row.byPolicy.get(a.policy_id);
                  return (
                    <td key={a.policy_id} className="px-3 py-2">
                      {cell ? (
                        <button
                          type="button"
                          onClick={() => onDrilldown(cell)}
                          className="hover:underline text-left"
                        >
                          <AssertionBadge assertion={cell.assertion} count={cell.violation_count} />
                        </button>
                      ) : (
                        <span className="text-ink-300 text-xs">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {notAssessed.noInventory.length > 0 && (
              <tr data-testid="compliance-not-assessed">
                <td colSpan={assignments.length + 1} className="px-3 py-2 text-xs text-ink-500 italic">
                  Not assessed — no cryptographic inventory ({notAssessed.noInventory.length} {notAssessed.noInventory.length === 1 ? 'asset' : 'assets'}): {names(notAssessed.noInventory)}
                </td>
              </tr>
            )}
            {notAssessed.other.length > 0 && (
              <tr>
                <td colSpan={assignments.length + 1} className="px-3 py-2 text-xs text-ink-500 italic">
                  No compliance result in the latest run ({notAssessed.other.length} {notAssessed.other.length === 1 ? 'asset' : 'assets'}): {names(notAssessed.other)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AssertionBadge({ assertion, count }: { assertion: ComplianceAssertion['assertion']; count: number }) {
  const styles: Record<typeof assertion, string> = {
    Compliant: 'bg-green-50 text-green-700 border-green-200',
    Non_compliant: 'bg-red-50 text-red-700 border-red-200',
    NA: 'bg-ink-50 text-ink-400 border-ink-200',
    Manual_override: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const label = assertion === 'Non_compliant' ? `${count} viol.` : assertion.replace('_', ' ');
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${styles[assertion]}`}>{label}</span>
  );
}

function AddPolicyModal({
  projectId, existingAssignments, onClose, onAdded,
}: {
  projectId: string;
  existingAssignments: PolicyAssignment[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const policiesQuery = useQuery<{ data: PolicyOption[] }>({
    queryKey: ['available-policies'],
    queryFn: () => api.get<PolicyOption[]>(`/policies`).then(data => ({ data })),
  });

  const addMut = useMutation({
    mutationFn: async (policyId: string) => {
      return api.post(`/projects/${projectId}/policy-assignments`, { policy_id: policyId });
    },
    onSuccess: () => onAdded(),
    onError: (err: unknown) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('Failed to add policy');
    },
  });

  const assignedIds = new Set(existingAssignments.map((a) => a.policy_id));
  const available = policiesQuery.data?.data?.filter((p) => !assignedIds.has(p.id)) ?? [];

  return (
    <Modal open={true} title="Add policy" onClose={onClose}>
      {policiesQuery.isLoading ? (
        <div className="text-sm text-ink-500 italic">Loading policies…</div>
      ) : policiesQuery.isError ? (
        <div className="text-sm text-red-700">
          Failed to load policies. Please try again or contact support.
        </div>
      ) : available.length === 0 ? (
        <div className="text-sm text-ink-500">All available policies are already assigned.</div>
      ) : (
        <div>
          <p className="text-sm text-ink-500 mb-3">
            Choose a policy to evaluate this project's assets against:
          </p>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full px-3 py-2 border border-ink-200 rounded text-sm mb-3"
          >
            <option value="">— select a policy —</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.short_code} ({p.kb_origin}) — v{p.version} · {p.rule_count} rules
              </option>
            ))}
          </select>
          {error && <div className="text-sm text-red-700 mb-3">{error}</div>}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-sm border border-ink-200 rounded hover:bg-ink-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => selectedId && addMut.mutate(selectedId)}
              disabled={!selectedId || addMut.isPending}
              className="px-3 py-1 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
            >
              {addMut.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DrilldownModal({ assertion, onClose }: { assertion: ComplianceAssertion; onClose: () => void }) {
  const detail = assertion.evaluation_details_json;
  const violations = detail?.violations ?? [];
  const conditionals = detail?.conditionals ?? [];
  const evaluations = detail?.evaluations ?? [];

  return (
    <Modal
      open={true}
      title={`${assertion.asset_name} — ${assertion.policy_name}`}
      onClose={onClose}
      widthClass="max-w-4xl"
    >
      <div className="text-sm">
        <div className="mb-4 flex items-baseline gap-3 flex-wrap">
          <AssertionBadge assertion={assertion.assertion} count={assertion.violation_count} />
          <span className="text-ink-500 text-xs">
            {assertion.violation_count} violation{assertion.violation_count !== 1 ? 's' : ''} ·{' '}
            {assertion.rule_count} rule{assertion.rule_count !== 1 ? 's' : ''} evaluated
            {(detail?.conditional_count ?? 0) > 0 && (
              <> · {detail!.conditional_count} conditional{detail!.conditional_count !== 1 ? 's' : ''}</>
            )}
          </span>
          <span className="text-ink-400 text-xs ml-auto font-mono">
            {assertion.policy_short_code}
          </span>
        </div>

        {/* Hard violations — most important, surface first */}
        {violations.length > 0 && (
          <section className="mb-5">
            <h3 className="text-sm font-display text-red-700 mb-2">
              {violations.length} Violation{violations.length !== 1 ? 's' : ''}
            </h3>
            <ul className="space-y-3">
              {violations.map((v, i) => (
                <ViolationCard key={i} v={v} kind="violation" />
              ))}
            </ul>
          </section>
        )}

        {/* Conditional rules — applied with a caveat */}
        {conditionals.length > 0 && (
          <section className="mb-5">
            <h3 className="text-sm font-display text-amber-700 mb-2">
              {conditionals.length} Conditional finding{conditionals.length !== 1 ? 's' : ''}
            </h3>
            <p className="text-xs text-ink-500 mb-2">
              These artifacts trigger a Conditional rule — allowed under specific
              circumstances described by the policy. Review the condition text below.
            </p>
            <ul className="space-y-3">
              {conditionals.map((v, i) => (
                <ViolationCard key={i} v={v} kind="conditional" />
              ))}
            </ul>
          </section>
        )}

        {/* All applied rules — Compliant case + audit trail for all cases */}
        {evaluations.length > 0 && (
          <section>
            <h3 className="text-sm font-display text-ink mb-2">
              All rule applications ({evaluations.length})
            </h3>
            <p className="text-xs text-ink-500 mb-2">
              Every (artifact × rule) pair the scorer evaluated for this policy. Useful
              for auditing the logic and confirming nothing was missed.
            </p>
            <div className="border border-ink-200 rounded overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-2 py-1 text-left">Artifact</th>
                    <th className="px-2 py-1 text-left">Type</th>
                    <th className="px-2 py-1 text-left">Algorithm</th>
                    <th className="px-2 py-1 text-left">Rule</th>
                    <th className="px-2 py-1 text-left">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {evaluations.map((e, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1">
                        <div className="font-medium text-ink">{e.artifact_label}</div>
                      </td>
                      <td className="px-2 py-1 text-ink-600">{formatArtifactType(e.artifact_type)}</td>
                      <td className="px-2 py-1 font-mono text-ink-700">
                        {e.artifact_algorithm ?? <span className="text-ink-300">—</span>}
                        {e.artifact_key_length !== null && `-${e.artifact_key_length}`}
                      </td>
                      <td className="px-2 py-1 font-mono text-ink-500">
                        {e.rule_algorithm ?? 'any'} · {e.rule_usage_scope} · {e.rule_verdict.replace('_', ' ')}
                      </td>
                      <td className="px-2 py-1">
                        <OutcomePill outcome={e.outcome} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {assertion.assertion === 'Compliant' && evaluations.length === 0 && (
          <div className="border border-dashed border-ink-200 rounded p-6 text-center">
            <p className="text-ink-500 text-sm">
              No rules from this policy applied to this asset's inventory. Compliant by
              vacuity (the policy doesn't constrain this asset's algorithm/usage mix).
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ViolationCard({ v, kind }: { v: ViolationDetail; kind: 'violation' | 'conditional' }) {
  const accent =
    kind === 'violation'
      ? 'border-red-300 bg-red-50/30'
      : 'border-amber-300 bg-amber-50/30';
  return (
    <li className={`border-l-4 ${accent} pl-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-3`}>
      {/* What we found */}
      <div>
        <div className="text-xs uppercase tracking-wide text-ink-400 mb-1">
          Artifact in inventory
        </div>
        <div className="font-medium text-sm text-ink">{v.artifact_label}</div>
        <div className="text-xs text-ink-500 mt-0.5">
          <span className="text-ink-400">type:</span> {formatArtifactType(v.artifact_type)}
          {' · '}
          <span className="text-ink-400">algorithm:</span>{' '}
          <span className="font-mono">
            {v.artifact_algorithm ?? '(none)'}
            {v.artifact_key_length !== null && `-${v.artifact_key_length}`}
            {v.artifact_curve && ` (${v.artifact_curve})`}
          </span>
        </div>
      </div>
      {/* What the policy says */}
      <div>
        <div className="text-xs uppercase tracking-wide text-ink-400 mb-1">
          Policy rule violated
        </div>
        <div className="font-mono text-sm text-ink">
          {v.rule_algorithm ?? 'any'} · {v.rule_usage_scope} ·{' '}
          <span className={kind === 'violation' ? 'text-red-700' : 'text-amber-700'}>
            {v.rule_verdict.replace('_', ' ')}
          </span>
        </div>
        {(v.rule_min_key_size !== null || v.rule_max_key_size !== null) && (
          <div className="text-xs text-ink-500 mt-0.5">
            Key size: {v.rule_min_key_size ?? 0}–{v.rule_max_key_size ?? '∞'}
          </div>
        )}
        {v.rule_condition_text && (
          <div className="text-xs text-ink-600 mt-1 italic">{v.rule_condition_text}</div>
        )}
        {v.rule_source_citation && (
          <div className="text-xs text-ink-400 mt-1">Source: {v.rule_source_citation}</div>
        )}
        <div className="text-xs text-ink-500 mt-1">
          <strong>Why:</strong> {v.message}
        </div>
      </div>
    </li>
  );
}

function formatArtifactType(type: string): string {
  // Map internal artifact_type codes to friendly labels.
  // certificate_sig / certificate_hash / certificate_pk are three sub-views
  // of the same certificate row, so we collapse the user-visible label.
  const labels: Record<string, string> = {
    key: 'Key',
    certificate_sig: 'Certificate (sig algo)',
    certificate_hash: 'Certificate (hash algo)',
    certificate_pk: 'Certificate (pubkey)',
    certificate: 'Certificate',
    signature: 'Signature',
    cipher_suite: 'Cipher suite',
    protocol: 'Protocol',
    library: 'Library',
    store: 'Key store',
  };
  return labels[type] ?? type;
}

function OutcomePill({ outcome }: { outcome: EvaluationDetail['outcome'] }) {
  const styles = {
    Pass: 'bg-green-50 text-green-700 border-green-200',
    Violation: 'bg-red-50 text-red-700 border-red-200',
    Conditional: 'bg-amber-50 text-amber-700 border-amber-200',
  } as const;
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${styles[outcome]}`}>
      {outcome}
    </span>
  );
}